import { createAction, createReducer } from 'utils/reducer';

// Local constants
const PATH = 'quote/';

const DISABLE_ERRORS = `${PATH}DISABLE_ERRORS`;
const RESET = `${PATH}RESET`;
const CHANGE_CONTROLS = `${PATH}CHANGE_CONTROLS`;
const CHANGE_CONTROLS_2 = `${PATH}CHANGE_CONTROLS_2`;
const CHANGE_STATE = `${PATH}CHANGE_STATE`;
const CHANGE_COST_CHOICE = `${PATH}CHANGE_COST_CHOICE`;
const CONFIRM_COST_CHOICE = `${PATH}CONFIRM_COST_CHOICE`;

// Global constants for saga
export const QUOTE = {
  GET_STATE_FROM_API: `${PATH}GET_STATE_FROM_API`,
  GET_STATE_FROM_API_SUCCESS: `${PATH}GET_STATE_FROM_API_SUCCESS`,
  GET_STATE_FROM_API_FAIL: `${PATH}GET_STATE_FROM_API_FAIL`,
  GET_MATERIAL: `${PATH}GET_MATERIAL`,
  GET_MATERIAL_SUCCESS: `${PATH}GET_MATERIAL_SUCCESS`,
  GET_MATERIAL_FAIL: `${PATH}GET_MATERIAL_FAIL`,
  SUBMIT_QUOTE: `${PATH}SUBMIT_QUOTE`,
  SUBMIT_QUOTE_SUCCESS: `${PATH}SUBMIT_QUOTE_SUCCESS`,
  SUBMIT_QUOTE_FAIL: `${PATH}SUBMIT_QUOTE_FAIL`,
  GET_PDF_FILE: `${PATH}GET_PDF_FILE`,
  GET_PDF_FILE_SUCCESS: `${PATH}GET_PDF_FILE_SUCCESS`,
  GET_PDF_FILE_FAIL: `${PATH}GET_PDF_FILE_FAIL`,
  CHECK_ZCOR_VERSIONS: `${PATH}CHECK_ZCOR_VERSIONS`,
  CHECK_ZCOR_VERSIONS_SUCCESS: `${PATH}CHECK_ZCOR_VERSIONS_SUCCESS`,
  CHECK_ZCOR_VERSIONS_FAIL: `${PATH}CHECK_ZCOR_VERSIONS_FAIL`,
  GET_CLIENT_BY_CODE: `${PATH}GET_CLIENT_BY_CODE`,
  GET_CLIENT_BY_CODE_SUCCESS: `${PATH}GET_CLIENT_BY_CODE_SUCCESS`,
  GET_CLIENT_BY_CODE_FAIL: `${PATH}GET_CLIENT_BY_CODE_FAIL`,
};

// actions
export const quoteActions = {
  reset: createAction(RESET),
  disableErrors: createAction(DISABLE_ERRORS),
  changeControls: createAction(CHANGE_CONTROLS, 'controls', 'invalidControls'),
  changeControls2: createAction(CHANGE_CONTROLS_2, 'key', 'value'),
  changeState: createAction(CHANGE_STATE, 'value'),
  changeCostChoice: createAction(CHANGE_COST_CHOICE, 'value'),
  confirmCostChoice: createAction(CONFIRM_COST_CHOICE),
  // api
  getStateFromApi: createAction(QUOTE.GET_STATE_FROM_API),
  getStateFromApiSuccess: createAction(QUOTE.GET_STATE_FROM_API_SUCCESS, 'payload'),
  getStateFromApiFail: createAction(QUOTE.GET_STATE_FROM_API_FAIL, 'payload'),
  getMaterial: createAction(QUOTE.GET_MATERIAL, 'data'),
  getMaterialSuccess: createAction(QUOTE.GET_MATERIAL_SUCCESS, 'payload'),
  getMaterialFail: createAction(QUOTE.GET_MATERIAL_FAIL, 'payload'),
  submitQuote: createAction(QUOTE.SUBMIT_QUOTE, 'data'),
  submitQuoteSuccess: createAction(QUOTE.SUBMIT_QUOTE_SUCCESS, 'payload'),
  submitQuoteFail: createAction(QUOTE.SUBMIT_QUOTE_FAIL, 'payload'),
  getPdfFile: createAction(QUOTE.GET_PDF_FILE, 'payload'),
  getPdfFileSuccess: createAction(QUOTE.GET_PDF_FILE_SUCCESS, 'payload'),
  getPdfFileFail: createAction(QUOTE.GET_PDF_FILE_FAIL, 'payload'),
  checkZcorVersions: createAction(QUOTE.CHECK_ZCOR_VERSIONS, 'data'),
  checkZcorVersionsSuccess: createAction(QUOTE.CHECK_ZCOR_VERSIONS_SUCCESS, 'payload'),
  checkZcorVersionsFail: createAction(QUOTE.CHECK_ZCOR_VERSIONS_FAIL, 'payload'),
  getClientByCode: createAction(QUOTE.GET_CLIENT_BY_CODE, 'data'),
  getClientByCodeSuccess: createAction(QUOTE.GET_CLIENT_BY_CODE_SUCCESS, 'payload'),
  getClientByCodeFail: createAction(QUOTE.GET_CLIENT_BY_CODE_FAIL, 'payload'),
};

const defaultControls = {
  title: '',
  clientCode: '',
  clientName: '',
  code: '',
  startDate: null,
  // La fecha final ya no se ingresa: se calcula como fecha inicial + validez en días.
  validityDays: '',
  materialLines: [],
  atn: '',
  paymentCondition: '',
  comments: '',
  currencyId: '1',
  delivery: '',
  sender: '',
  phone: '',
  email: '',
};

const defaultInvalidControls = {
  name: false,
};

const defaultCostChoice = {
  open: false,
  lineIndex: null,
  zcotProjectedCost: 0,
  zcorVersion: null,
  selected: 'ZCOT',
  // true cuando el popup se reabre para una línea ya agregada cuyo ZCOR cambió.
  refresh: false,
  material: '',
};

// Lo mínimo para saber después si la versión ZCOR de la línea quedó desactualizada.
const toOfferedVersion = (version) =>
  version ? { id: version.id, totalProjectedCost: version.totalProjectedCost } : null;

const sameCost = (a, b) => Math.round(Number(a) * 10000) === Math.round(Number(b) * 10000);

// La línea guarda la versión que se le ofreció al agregarla (o al confirmar el popup). Si desde
// entonces se guardó otra versión, o la misma cambió de costo, hay que volver a preguntar.
const isZcorOutdated = (line, latestVersion) => {
  if (!latestVersion) return false;
  const offered = line.offeredZcorVersion;
  return (
    !offered ||
    offered.id !== latestVersion.id ||
    !sameCost(offered.totalProjectedCost, latestVersion.totalProjectedCost)
  );
};

const buildRefreshChoice = (line, lineIndex, zcorVersion) => ({
  open: true,
  lineIndex,
  zcotProjectedCost: line.zcotProjectedCost,
  zcorVersion,
  selected: line.formulaVersionId ? 'ZCOR' : 'ZCOT',
  refresh: true,
  material: `${line.m_code} - ${line.name}`,
});

// Saca de la cola el siguiente popup pendiente cuya línea siga existiendo.
const nextCostChoice = (lines, pending) => {
  const queue = [...pending];
  while (queue.length) {
    const { mCode, zcorVersion } = queue.shift();
    const lineIndex = lines.findIndex((line) => line.m_code === mCode);
    if (lineIndex !== -1) {
      return {
        costChoice: buildRefreshChoice(lines[lineIndex], lineIndex, zcorVersion),
        pendingCostChoices: queue,
      };
    }
  }
  return { costChoice: { ...defaultCostChoice }, pendingCostChoices: [] };
};

const initialState = {
  error: false,
  errorMsg: '',
  controls: {
    ...defaultControls,
  },
  invalidControls: {
    ...defaultInvalidControls,
  },
  step: 0,
  linesHasChanged: false,
  newQuoteId: '',
  costChoice: { ...defaultCostChoice },
  pendingCostChoices: [],
  // Resultado de buscar el código de cliente ingresado; `code` dice a qué código corresponde.
  clientLookup: { code: '', name: '', notFound: false },
};

const quote = createReducer(initialState, {
  [DISABLE_ERRORS]: (state) => ({
    ...state,
    error: false,
    errorMsg: '',
  }),
  [RESET]: (state) => ({
    ...initialState,
    controls: {
      ...defaultControls,
      currencyId: state.controls.currencyId,
    },
  }),
  [CHANGE_STATE]: (state, action) => ({
    ...state,
    ...action.value,
  }),
  [CHANGE_CONTROLS]: (state, action) => ({
    ...state,
    controls: {
      ...state.controls,
      ...action.controls,
    },
    invalidControls: {
      ...state.invalidControls,
      ...(action.invalidControls || {}),
    },
  }),
  [CHANGE_CONTROLS_2]: (state, action) => ({
    ...state,
    controls: {
      ...state.controls,
      [action.key]: action.value,
    },
  }),
  [QUOTE.GET_MATERIAL_SUCCESS]: (state, action) => {
    const lines = state.controls.materialLines;
    const { mainMaterial, totals, costsDiffer, latestZcorVersion } = action.payload;
    const newLines = [
      ...lines,
      {
        material_id: mainMaterial.id,
        m_code: mainMaterial.c_material,
        name: mainMaterial.description,
        dosis: mainMaterial.dosis,
        kardex: totals.kardexDolar, // TODO: cambiar de manera condicional si se elije pesos o dólares.
        projectedCost: totals.totalProjectedCost,
        // ZCOT vigente al momento de cotizar: se guarda aparte y nunca se sobreescribe,
        // para poder mostrarlo como referencia aunque el usuario termine eligiendo ZCOR.
        zcotProjectedCost: totals.totalProjectedCost,
        zcot: totals.zcotDolar,
        difCost: totals.diffCost,
        calculatedPrice: totals.totalCostDolar, // Kardex calculado
        umb: mainMaterial.umb,
        // mPercPpto: getMPercPpto(lines, totals.kardexDolar),
        mPercPpto: 0,
        mgPerc: null,
        price: 0,
        volumeKg: null,
        totalMargin: 0,
        totalSale: 0,
        // Se reinicia siempre a ZCOT; solo pasa a la versión ZCOR si el usuario la elige en el popup.
        formulaVersionId: null,
        formulaVersionLabel: null,
        offeredZcorVersion: toOfferedVersion(latestZcorVersion),
      },
    ];
    return {
      ...state,
      linesHasChanged: true,
      controls: {
        ...state.controls,
        materialLines: newLines,
        code: '',
      },
      costChoice: costsDiffer
        ? {
            open: true,
            lineIndex: newLines.length - 1,
            zcotProjectedCost: totals.totalProjectedCost,
            zcorVersion: latestZcorVersion,
            selected: 'ZCOT',
            refresh: false,
            material: '',
          }
        : state.costChoice,
    };
  },
  [CHANGE_COST_CHOICE]: (state, action) => ({
    ...state,
    costChoice: {
      ...state.costChoice,
      selected: action.value,
    },
  }),
  [CONFIRM_COST_CHOICE]: (state) => {
    const { lineIndex, selected, zcorVersion } = state.costChoice;
    // Elegir ZCOT también tiene que revertir la línea: al refrescar puede venir usando un ZCOR.
    const newLines = state.controls.materialLines.map((line, idx) => {
      if (idx !== lineIndex) return line;
      const useZcor = selected === 'ZCOR' && zcorVersion;
      const projectedCost = useZcor ? zcorVersion.totalProjectedCost : line.zcotProjectedCost;
      return {
        ...line,
        projectedCost,
        formulaVersionId: useZcor ? zcorVersion.id : null,
        formulaVersionLabel: useZcor ? zcorVersion.versionLabel : null,
        difCost: line.kardex ? projectedCost / line.kardex - 1 : line.difCost,
        offeredZcorVersion: toOfferedVersion(zcorVersion),
      };
    });
    return {
      ...state,
      controls: { ...state.controls, materialLines: newLines },
      ...nextCostChoice(newLines, state.pendingCostChoices),
    };
  },
  [QUOTE.CHECK_ZCOR_VERSIONS_SUCCESS]: (state, action) => {
    const { versions } = action.payload;
    const lines = state.controls.materialLines;
    const openMCode = state.costChoice.open ? lines[state.costChoice.lineIndex]?.m_code : null;
    const alreadyQueued = [openMCode, ...state.pendingCostChoices.map((choice) => choice.mCode)];
    const outdated = lines
      .filter(
        (line) =>
          !alreadyQueued.includes(line.m_code) && isZcorOutdated(line, versions[line.m_code]),
      )
      .map((line) => ({ mCode: line.m_code, zcorVersion: versions[line.m_code] }));
    if (!outdated.length) return state;
    const pendingCostChoices = [...state.pendingCostChoices, ...outdated];
    if (state.costChoice.open) return { ...state, pendingCostChoices };
    return { ...state, ...nextCostChoice(lines, pendingCostChoices) };
  },
  [QUOTE.GET_CLIENT_BY_CODE_SUCCESS]: (state, action) => {
    const { client } = action.payload;
    return {
      ...state,
      clientLookup: {
        code: action.payload.clientCode,
        name: client?.name || '',
        notFound: !client,
      },
    };
  },
  [QUOTE.SUBMIT_QUOTE_SUCCESS]: (state, action) => ({
    ...state,
    step: 2,
    newQuoteId: action.payload.quoteId,
  }),
});

export default quote;
